import { useState, useCallback, useMemo } from 'react';
import { useQueryParams } from '@atlassian/jira-software-router-utils';
import { localStorage } from '../../common/utils/storage';

const hasNonEmptyKey = (object: Record<string, string>) =>
	Object.entries(object).some((entry) => entry[1] && entry[1] !== '');

/**
 * Hook that checks whether any of a set of provided query parameters had a non-empty value on render
 * @param queryParamsToWatch an array of strings representing query param keys to watch
 * @returns boolean indicating whether any of the `queryParamsToWatch` had a non-empty value on render
 */
export const useHasQueryParamOnRender = (queryParamsToWatch: string[]): boolean => {
	const [queryParams] = useQueryParams(...queryParamsToWatch);
	const hasQueryParamsOnRender = useMemo(
		() => hasNonEmptyKey(queryParams),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);
	return hasQueryParamsOnRender;
};

export const useLocalStorage = <T,>(
	key: string,
	init: T[],
): [T, (arg1: T | ((currentVal: T) => T)) => void] => {
	const [storedValue, setStoredValue] = useState<T>(localStorage.get(key) ?? init);
	const setValue = useCallback(
		(value: T | ((currentValue: T) => T)) => {
			// @ts-expect-error - TS2349 - This expression is not callable.
			const valueToStore: T = typeof value === 'function' ? value(storedValue) : value;
			setStoredValue(valueToStore);
			localStorage.set(key, valueToStore);
		},
		[key, storedValue],
	);
	return [storedValue, setValue];
};

export { useRetryableError } from './use-retryable-error';
